import { CommonModule } from "@angular/common";
import {
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  input,
  signal,
  untracked,
  viewChild,
} from "@angular/core";
import Chart from "chart.js/auto";
import type { ChartConfiguration, ChartDataset } from "chart.js";
import { Widget } from "../../../dashboard/models/dashboard.model";
import { EntityMetadata } from "../../../../shared/store/store-types.model";
import { WidgetTitleComponent } from "../../../../shared/components/widget-title/widget-title.component";
import { ChipsToggleComponent } from "../../../../shared/modules/chips-toggle/chips-toggle.component";
import { ChipsComponent } from "../../../../shared/modules/chips/chips.component";
import { PopoverModule } from "../../../../shared/modules/popover/popover.module";
import { SharedModule } from "../../../../shared/shared.module";
import { ArticleFacadeService } from "../../services/article/article-facade.service";
import { UserInfoCardComponent } from "../../../../shared/components/user-info-card/user-info-card.component";
import { parseNumber } from "../../../../shared/utils/number-utils.service";

// ---- Types ----
type ViewType = "PIE" | "LIST";
type SortBy = "HEURES" | "ALPHA";

// ---- Modèle attendu dans stats ----
// stats().travail: { duree: string; userInfosId: string; ofId?: string }[]
// stats().ofs:     { id: string; ... }[]     (pour filtrer les travaux croisés aux OFs présents)

@Component({
  selector: "vl-heures-par-user",
  standalone: true,
  imports: [
    CommonModule,
    WidgetTitleComponent,
    ChipsToggleComponent,
    SharedModule,
    ChipsComponent,
    PopoverModule,
    UserInfoCardComponent,
  ],
  templateUrl: "./heures-par-user.component.html",
  styleUrls: ["./heures-par-user.component.scss"],
  host: { class: "widget-component" },
})
export class HeuresParUserComponent {
  private articleFacade = inject(ArticleFacadeService);
  stats = this.articleFacade.getState().stats;

  widget = input<Widget>();
  metadata = input<EntityMetadata>();

  // Canvas
  canvasRef = viewChild<ElementRef<HTMLCanvasElement>>("heuresChart");
  chart?: Chart<"doughnut", number[], string>;

  // Légende externe
  legendLabels = signal<
    { label: string; percentage: string; color: string }[] | null
  >(null);

  // UI
  viewType = signal<ViewType>("PIE");
  sortBy = signal<SortBy>("HEURES");

  elementsView = [
    { icon: "pie_chart", value: "PIE" },
    { icon: "list", value: "LIST" },
  ];
  elementsSort = [
    { name: "Tri Heures", value: "HEURES" },
    { name: "Tri Alpha", value: "ALPHA" },
  ];

  // Filtres
  topCount = signal<number>(12);
  search = signal<string>("");

  // ---------- Utils durée ----------
  /** Accepte "HH:MM", "HHhMM", "H.MM" etc. Retourne des minutes. */
  private parseDureeToMinutes(s: unknown): number {
    if (!s) return 0;
    const str = String(s).trim();

    // "07h00" / "7h5"
    let m = /^(\d{1,3})\s*h\s*(\d{1,2})?$/i.exec(str);
    if (m) {
      const hh = parseNumber(m[1] || 0);
      const mm = parseNumber(m[2] || 0);
      return hh * 60 + mm;
    }

    // "07:00" / "7:5"
    m = /^(\d{1,3}):(\d{1,2})$/.exec(str);
    if (m) {
      const hh = parseNumber(m[1] || 0);
      const mm = parseNumber(m[2] || 0);
      return hh * 60 + mm;
    }

    // "7.5" heures décimales -> 7h30
    m = /^(\d+(?:[.,]\d+)?)$/.exec(str);
    if (m) {
      const hours = parseNumber(m[1].replace(",", "."));
      return Math.round(hours * 60);
    }

    return 0;
  }

  formatMinutes(totalMin: number): string {
    const min = Math.max(0, Math.round(totalMin));
    const h = Math.floor(min / 60);
    const m = min % 60;
    const mm = String(m).padStart(2, "0");
    return `${h}h${mm}`;
  }

  // ---------- Données ----------
  // Filtre les travaux aux seuls OF présents dans stats().ofs (croisement demandé)
  private travauxFiltres = computed(() => {
    const s = this.stats();
    const travaux = (s?.travail ?? []) as Array<{
      duree: string;
      userInfosId: string;
      ofId?: string;
    }>;
    const ofIds = new Set((s?.ofs ?? []).map((o: any) => o.id));
    if (!travaux?.length) return [];
    return travaux.filter((t) => !t.ofId || ofIds.has(t.ofId)); // si ofId absent, on accepte; sinon on croise.
  });

  // Agrégation par userInfosId
  private baseRows = computed(() => {
    const map = new Map<string, number>(); // userId -> minutes
    for (const t of this.travauxFiltres()) {
      const id = t.userInfosId || "Inconnu";
      const minutes = this.parseDureeToMinutes(t.duree);
      map.set(id, (map.get(id) ?? 0) + minutes);
    }
    return Array.from(map.entries()).map(([userId, minutes]) => ({
      key: userId,
      label: userId, // TODO: remplacer par displayName si disponible
      minutes,
    }));
  });

  // Recherche
  private filtered = computed(() => {
    const q = this.search().trim().toLowerCase();
    if (!q) return this.baseRows();
    return this.baseRows().filter((r) => r.label.toLowerCase().includes(q));
  });

  // Tri
  private sorted = computed(() => {
    const by = this.sortBy();
    const arr = [...this.filtered()];
    arr.sort((a, b) => {
      switch (by) {
        case "ALPHA":
          return a.label.localeCompare(b.label, "fr");
        case "HEURES":
        default:
          return b.minutes - a.minutes;
      }
    });
    return arr;
  });

  // Top N
  maxRows = computed(() => this.filtered().length);
  private top = computed(() => {
    const n = Math.max(1, Math.min(this.topCount(), this.maxRows()));
    return this.sorted().slice(0, n);
  });

  // ===== Totaux & « Autres » =====
  private totalAll = computed(() =>
    this.filtered().reduce((a, x) => a + x.minutes, 0),
  );
  private totalTopOnly = computed(() =>
    this.top().reduce((a, x) => a + x.minutes, 0),
  );
  private othersValue = computed(() =>
    Math.max(0, this.totalAll() - this.totalTopOnly()),
  );
  private hasOthers = computed(
    () =>
      this.filtered().length >
      Math.max(1, Math.min(this.topCount(), this.maxRows())),
  );

  // Données pour le PIE/LIST
  private pieLabels = computed(() => {
    const labs = this.top().map((x) => x.label);
    if (this.hasOthers()) labs.push("Autres");
    return labs;
  });

  // ⚠️ public pour le template (empty state)
  values = computed(() => {
    const vals = this.top().map((x) => x.minutes);
    if (this.hasOthers()) vals.push(this.othersValue());
    return vals;
  });

  // LIST
  private total = computed(() => this.values().reduce((a, b) => a + b, 0)); // inclut "Autres"
  totalTop = () => this.totalTopOnly();
  totalTopPct = () => {
    const all = this.totalAll();
    return all > 0 ? (this.totalTopOnly() / all) * 100 : 0;
  };

  listRows = computed(() => {
    const totAll = this.total() || 0;
    return this.top().map((x) => {
      const value = x.minutes;
      const pct = totAll > 0 ? (value / totAll) * 100 : 0;
      return { key: x.key, label: x.label, value, pct };
    });
  });

  // ===== Palette =====
  private makeColors(n: number): string[] {
    const base = [140, 210, 260, 275, 40, 0, 195, 220];
    const out: string[] = [];
    for (let i = 0; i < n; i++) {
      const h =
        base[i % base.length] +
        (i >= base.length ? 12 * Math.floor(i / base.length) : 0);
      out.push(`hsl(${h % 360} 85% 55%)`);
    }
    return out;
  }

  private colors = computed(() => this.makeColors(this.values().length));

  // ===== Helpers =====
  private arrEq<T>(a?: T[] | null, b?: T[] | null): boolean {
    if (!a && !b) return true;
    if (!a || !b) return false;
    if (a.length !== b.length) return false;
    for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false;
    return true;
  }

  // ===== Effects / Chart =====
  constructor() {
    // Clamp du slider
    effect(
      () => {
        // 1) Reset sur changement de stats
        this.stats(); // dépendance voulue
        const max = untracked(() => this.maxRows()) || 1; // ne pas déclencher sur maxRows
        this.topCount.set(Math.min(10, max));
      },
      { allowSignalWrites: true },
    );

    effect(
      () => {
        // 2) Clamp quand maxRows évolue
        const max = this.maxRows() || 1;
        this.topCount.update((v) => Math.min(Math.max(1, v || 1), max));
      },
      { allowSignalWrites: true },
    );

    // Live update chart + legend
    effect(
      () => {
        if (this.viewType() !== "PIE") return;
        const canvas = this.canvasRef();
        if (!canvas || !this.chart) return;

        const labels = this.pieLabels();
        const values = this.values();
        const ds = this.chart.data.datasets[0] as ChartDataset<
          "doughnut",
          number[]
        >;
        const prevLabels = (this.chart.data.labels ?? []) as string[];
        const prevValues = (ds.data ?? []) as number[];

        this.chart.data.labels = labels;
        ds.data = values;

        if (!this.arrEq(values, prevValues)) {
          ds.backgroundColor = this.colors();
        }
        if (
          !this.arrEq(labels, prevLabels) ||
          !this.arrEq(values, prevValues)
        ) {
          this.chart.update(); // triggers generateLabels -> updates legendLabels
        }
      },
      { allowSignalWrites: true },
    );

    // Build / destroy chart on view/canvas/data
    effect(
      () => {
        if (this.viewType() !== "PIE") {
          this.destroyChart(true);
          return;
        }
        const canvas = this.canvasRef();
        if (!canvas) {
          this.destroyChart(true);
          return;
        }
        if (!this.values().length) {
          this.destroyChart(true);
          return;
        }
        this.buildChart();
      },
      { allowSignalWrites: true },
    );
  }

  private destroyChart(clearLegend = false) {
    if (clearLegend) this.legendLabels.set([]);
    if (this.chart) {
      this.chart.destroy();
      this.chart = undefined;
    }
  }

  private buildChart() {
    const canvas = this.canvasRef();
    if (!canvas) return;
    const ctx = canvas.nativeElement.getContext("2d");
    if (!ctx) return;

    this.destroyChart(true);

    const config: ChartConfiguration<"doughnut", number[], string> = {
      type: "doughnut",
      data: {
        labels: this.pieLabels(),
        datasets: [
          {
            label: "Heures",
            data: this.values(),
            backgroundColor: this.colors(),
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        animation: false,
        resizeDelay: 120,
        plugins: {
          legend: {
            display: true,
            position: "right",
            labels: {
              usePointStyle: true,
              generateLabels: (c) => {
                const data = c.data as any;
                const labels = (data.labels || []) as string[];
                const values = (data.datasets?.[0]?.data || []) as number[];
                const colors = (data.datasets?.[0]?.backgroundColor ||
                  []) as string[];
                const total = values.reduce((a, b) => +a + +b, 0);
                const out = labels.map((lbl, i) => {
                  const val = +values[i] || 0;
                  const pct =
                    total > 0 ? ((val / total) * 100).toFixed(0) : "0";
                  return {
                    label: lbl,
                    percentage: pct,
                    color: String(colors[i] ?? "#999"),
                  };
                });
                this.legendLabels.set(out);
                return []; // masque la légende native
              },
            },
          },
          tooltip: {
            bodyFont: { size: 12 },
            titleFont: { size: 12 },
            callbacks: {
              label: (ctx) => {
                const valMin = ctx.parsed as number;
                return ` ${ctx.label}: ${this.formatMinutes(valMin)}`;
              },
            },
          },
          title: { display: false },
        },
        layout: { padding: { top: 6, right: 6, bottom: 6, left: 6 } },
      },
    };
    this.chart = new Chart<"doughnut", number[], string>(ctx, config);
  }

  protected readonly Math = Math;
}
